import { camelizeKeys } from "humps";
import pick from "lodash/pick";
import { useBookingModuleStore, useCalendarRoot, useCalendarRootActions } from "PFApp/booking/store";
import { isWorkforceProfiles } from "PFApp/booking/utils/calendar_root.utils";
import { ProjectsResponse } from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import { WorkforceResponse } from "PFCore/hooks/queries/bookings/workforce/use_workforce_entries";
import { MetaWithPagination } from "PFTypes";
import { useEffect } from "react";

type UseCalendarRootMeta = {
  workforceData: WorkforceResponse | undefined;
  projectsData: ProjectsResponse | undefined;
};

export const useCalendarRootMeta = ({ workforceData, projectsData }: UseCalendarRootMeta) => {
  const calendarRoot = useCalendarRoot();
  const {
    setSelectedOrder,
    setTotalPages,
    setCounters,
    setAvailableOrderOptions,
    clearPendingFiltersMetaCallback
  } = useCalendarRootActions(calendarRoot);

  const pendingFiltersMetaCallback = useBookingModuleStore(
    (state) => state[calendarRoot].pendingFiltersMetaCallback
  );

  const updateMeta = (meta: MetaWithPagination) => {
    setTotalPages(meta.totalPages);
    setSelectedOrder(meta.order?.value ?? null);
    setAvailableOrderOptions(meta.order?.options ?? []);
  };

  const metaFilters = isWorkforceProfiles(calendarRoot)
    ? workforceData?.meta?.filtersV2
    : projectsData?.meta?.filtersV2;

  useEffect(() => {
    if (metaFilters && pendingFiltersMetaCallback) {
      pendingFiltersMetaCallback(metaFilters);
      clearPendingFiltersMetaCallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaFilters, pendingFiltersMetaCallback]);

  useEffect(() => {
    if (workforceData) {
      const counters = pick(
        camelizeKeys(workforceData.meta.counters),
        "filteredProfilesCount",
        "filteredActiveProfilesCount",
        "endpointLimit"
      );
      setCounters(counters);
      updateMeta(workforceData.meta);
    }
  }, [workforceData]);

  useEffect(() => {
    if (projectsData) {
      const counters = pick(
        camelizeKeys(projectsData.meta.counters),
        "filteredActivitiesCount",
        "endpointLimit"
      );
      setCounters(counters);
      updateMeta(projectsData.meta);
    }
  }, [projectsData]);
};
